<template>
  <b-modal
    size="lg"
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    :visible="visible"
    @change="$emit('update:visible', $event)"
  >
    <div class="iq-card mb-0" style="min-width: 300px;">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title">
          <h4 class="card-title">{{ headingText }}</h4>
        </div>
      </div>
      <div class="iq-card-body">
        <div class="row mb-3">
          <div
            v-for="field in shownFields"
            :key="field.key"
            role="group"
            class="form-group"
            :class="`col-md-${field.colMd || 6}`"
          >
            <label :for="field.key" class="d-block form-label text-left">
              {{ field.label }}:
            </label>
            <div class="bv-no-focus-ring">
              <div role="group" class="input-group">
                <v-select
                  v-if="field.type === 'select' && field.searchable"
                  :id="field.key"
                  class="searchable-select"
                  v-model="form[field.key]"
                  :options="optionsFor(field)"
                  :filterable="!isRemoteField(field)"
                  label="text"
                  :reduce="(option) => option.value"
                  :clearable="!field.required"
                  :placeholder="`Search ${field.label}...`"
                  :class="errors[field.key] ? 'is-invalid' : ''"
                  @search="(query, setLoading) => onFieldSearch(field, query, setLoading)"
                  @input="onSelectInput(field, $event)"
                />
                <b-form-select
                  v-else-if="field.type === 'select'"
                  :id="field.key"
                  class="form-control"
                  v-model="form[field.key]"
                  :options="optionsFor(field)"
                  :class="errors[field.key] ? 'is-invalid' : ''"
                  @change="reconcileFunctionOptions"
                />
                <b-form-textarea
                  v-else-if="field.type === 'textarea'"
                  :id="field.key"
                  class="form-control"
                  v-model="form[field.key]"
                  :placeholder="field.placeholder || field.label"
                  :class="errors[field.key] ? 'is-invalid' : ''"
                />
                <b-form-checkbox-group
                  v-else-if="field.type === 'checkbox-group'"
                  :id="field.key"
                  class="pt-2"
                  v-model="form[field.key]"
                  :options="optionsFor(field)"
                />
                <input
                  v-else
                  class="form-control"
                  :type="field.type === 'number' ? 'number' : 'text'"
                  :id="field.key"
                  :placeholder="field.placeholder || field.label"
                  v-model="form[field.key]"
                  :class="errors[field.key] ? 'is-invalid' : ''"
                >
                <b-form-invalid-feedback v-if="errors[field.key]" force-show>
                  {{ errors[field.key] }}
                </b-form-invalid-feedback>
                <small v-if="field.hint" class="form-text text-muted w-100">{{ field.hint }}</small>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <b-button
            @click="$emit('cancel')"
            class="mr-1"
            size="sm"
            :disabled="isSubmit"
            variant="outline-danger"
          >
            <div class="mx-2">Cancel</div>
          </b-button>
          <b-button
            type="submit"
            @click="onSubmit"
            :disabled="isSubmit"
            variant="primary"
            size="sm"
            class="ml-1"
          >
            <div class="mx-2">
              <i v-if="isSubmit" class="ri-loader-line"></i>
              Submit
            </div>
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { createMasterCrud, searchSourceOptions, findSourceOption } from '@/services/masterCrud';
import { buildMasterParams, SEARCH_DEBOUNCE_MS } from '@/util/master/masterQuery';
import { mergeSelectedOption } from '@/util/master/searchOptions';
import {
  visibleFields, buildPayload, splitSet,
} from '@/util/master/masterFields';

export default {
  name: 'MasterFormModal',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, required: true },
    fields: { type: Array, required: true },
    value: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    isSubmit: { type: Boolean, default: false },
    pkField: { type: String, required: true },
  },
  data() {
    return {
      form: {},
      sourceOptions: {},
      // field remote doang: label opsi terpilih, nomor urut anti-respons-basi, timer debounce
      remoteSelected: {},
      searchSeq: {},
      searchTimers: {},
    };
  },
  computed: {
    isEdit() {
      return !!this.value[this.pkField];
    },
    headingText() {
      return this.isEdit ? `Edit ${this.title}` : `Add New ${this.title}`;
    },
    shownFields() {
      return visibleFields(this.fields, this.form);
    },
  },
  watch: {
    visible(open) {
      if (!open) {
        Object.values(this.searchTimers).forEach(clearTimeout);
        return;
      }
      this.remoteSelected = {};
      this.form = this.buildForm();
      this.reconcileFunctionOptions();
      this.loadSourceOptions();
      this.loadRemoteFieldOptions();
    },
  },
  beforeDestroy() {
    Object.values(this.searchTimers).forEach(clearTimeout);
  },
  methods: {
    // source.searchField ada = field ini nyari ke backend, bukan filter list lokal
    isRemoteField(field) {
      return !!(field.searchable && field.source && field.source.searchField);
    },
    // Nilai lama di-snap kalau opsinya menyempit sampai gak valid lagi.
    reconcileFunctionOptions() {
      this.fields.forEach((field) => {
        if (field.type !== 'select' || typeof field.options !== 'function') return;
        const options = field.options(this.form);
        const stillValid = options.some((option) => option.value === this.form[field.key]);
        if (!stillValid && options.length === 1) this.$set(this.form, field.key, options[0].value);
      });
    },
    // Vue 2 gak reaktif ke key baru, jadi semua key diisi sekaligus di sini.
    buildForm() {
      const form = {};
      this.fields.forEach((field) => {
        const raw = this.value[field.key];
        if (field.type === 'checkbox-group') {
          form[field.key] = splitSet(raw === undefined || raw === null || raw === '' ? field.default : raw);
          return;
        }
        if (raw === undefined || raw === null || raw === '') {
          form[field.key] = field.default !== undefined ? field.default : '';
          return;
        }
        if (field.type === 'select' && this.isRemoteField(field)) {
          form[field.key] = raw;
          return;
        }
        form[field.key] = field.type === 'select' ? this.matchOption(field, raw) : raw;
      });
      return form;
    },
    // API balikin angka 1, opsi bisa string '1'. Cocokin longgar biar dropdown gak kosong.
    matchOption(field, raw) {
      const hit = this.optionsFor(field)
        .find((option) => String(option.value).toLowerCase() === String(raw).toLowerCase());
      return hit ? hit.value : raw;
    },
    optionsFor(field) {
      if (typeof field.options === 'function') return field.options(this.form);
      if (field.options) return field.options;
      if (this.isRemoteField(field)) {
        return mergeSelectedOption(this.sourceOptions[field.key] || [], this.remoteSelected[field.key]);
      }
      return this.sourceOptions[field.key] || [];
    },
    // Nilai select dari v-select: reconcile jalan sama kayak biasa, remote nambah bookkeeping label.
    onSelectInput(field, value) {
      this.reconcileFunctionOptions();
      if (!this.isRemoteField(field)) return;
      if (value === null || value === undefined || value === '') {
        this.$delete(this.remoteSelected, field.key);
        return;
      }
      const hit = (this.sourceOptions[field.key] || [])
        .find((option) => String(option.value) === String(value));
      this.$set(this.remoteSelected, field.key, hit || { value, text: String(value) });
    },
    // @search v-select: query di-debounce, respons basi diabaikan pakai nomor urut.
    onFieldSearch(field, query, setLoading) {
      if (!this.isRemoteField(field)) return;
      clearTimeout(this.searchTimers[field.key]);
      setLoading(true);
      this.searchTimers[field.key] = setTimeout(async () => {
        await this.refreshFieldOptions(field, query);
        setLoading(false);
      }, SEARCH_DEBOUNCE_MS);
    },
    async refreshFieldOptions(field, query) {
      this.searchSeq[field.key] = (this.searchSeq[field.key] || 0) + 1;
      const seq = this.searchSeq[field.key];
      try {
        const options = await searchSourceOptions(field.source, { query, max: 50 });
        if (seq === this.searchSeq[field.key]) this.$set(this.sourceOptions, field.key, options);
      } catch (e) {
        if (seq === this.searchSeq[field.key]) this.$set(this.sourceOptions, field.key, []);
      }
    },
    // Modal dibuka: muat 50 teratas, terus kalau nilai edit gak ada di situ, cari satu-satu.
    async loadRemoteFieldOptions() {
      const remoteFields = this.fields.filter(
        (field) => field.type === 'select' && this.isRemoteField(field),
      );
      if (!remoteFields.length) return;
      await Promise.all(remoteFields.map((field) => this.refreshFieldOptions(field, '')));
      await Promise.all(remoteFields.map((field) => this.ensureSelectedOption(field)));
    },
    async ensureSelectedOption(field) {
      const raw = this.form[field.key];
      if (raw === '' || raw === null || raw === undefined) return;
      const inList = (this.sourceOptions[field.key] || [])
        .some((option) => String(option.value) === String(raw));
      if (inList) return;
      try {
        const hit = await findSourceOption(field.source, raw);
        this.$set(this.remoteSelected, field.key, hit || { value: raw, text: String(raw) });
      } catch (e) {
        this.$set(this.remoteSelected, field.key, { value: raw, text: String(raw) });
      }
    },
    async loadSourceOptions() {
      const pending = this.fields.filter(
        (field) => field.type === 'select' && field.source && !this.isRemoteField(field)
          && !this.sourceOptions[field.key],
      );
      if (!pending.length) return;

      const loaded = { ...this.sourceOptions };
      await Promise.all(pending.map(async (field) => {
        const { master, keyField, displayField } = field.source;
        try {
          const body = await createMasterCrud(master).list(buildMasterParams({ page: 1, max: 1000 }));
          const rows = Array.isArray(body.data) ? body.data : [];
          loaded[field.key] = rows.map((row) => ({ value: row[keyField], text: row[displayField] }));
        } catch (e) {
          loaded[field.key] = [];
        }
      }));
      this.sourceOptions = loaded;

      // Opsinya baru nyampe, nilai edit dicocokin ulang.
      pending.forEach((field) => {
        const raw = this.value[field.key];
        if (raw === undefined || raw === null || raw === '') return;
        this.$set(this.form, field.key, this.matchOption(field, raw));
      });
    },
    onSubmit() {
      this.$emit('submit', buildPayload(this.fields, this.form, this.pkField));
    },
  },
};
</script>

<style scoped>
.searchable-select {
  width: 100%;
  padding: 0;
}
.searchable-select.is-invalid ::v-deep .vs__dropdown-toggle {
  border-color: #dc3545;
}
</style>
